import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined'
import { Box, Skeleton, type SxProps, type Theme, Typography, Stack } from '@mui/material'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useFileStatusQuery } from '../hooks/useFileStatusQuery'

export type FilestoreImageProps = {
  filestoreId: string
  alt: string
  documentId?: string
  loadingFallback?: ReactNode
  errorFallback?: ReactNode
  sx?: SxProps<Theme>
}

export const FilestoreImage = ({ filestoreId, documentId, ...props }: FilestoreImageProps) => (
  <FilestoreImageContent
    key={`${filestoreId}:${documentId ?? ''}`}
    filestoreId={filestoreId}
    documentId={documentId}
    {...props}
  />
)

const FilestoreImageContent = ({ filestoreId, alt, documentId, loadingFallback, errorFallback, sx }: FilestoreImageProps) => {
  const { t } = useTranslation('documents')
  const { signedUrl, validationStatus, isTimeout } = useFileStatusQuery({
    filestoreId,
    documentId,
    enabled: true,
    gcTime: 600_000,
    staleTime: 600_000,
  })
  const [hasErrored, setHasErrored] = useState(false)
  const isUnavailable = isTimeout || validationStatus === 'QUARANTINED' || validationStatus === 'NOT_FOUND'

  if (isUnavailable || hasErrored) {
    return (
      errorFallback ?? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.disabled', py: 1 }}>
          <BrokenImageOutlinedIcon fontSize="small" />
          <Typography variant="small-regular">{t('image.unavailable')}</Typography>
        </Stack>
      )
    )
  }

  if (!signedUrl) {
    return loadingFallback ?? <Skeleton variant="rounded" />
  }

  return (
    <Box
      component="img"
      src={signedUrl}
      alt={alt}
      sx={sx}
      onError={() => {
        setHasErrored(true)
      }}
    />
  )
}
